import React, { useEffect, useRef, useState } from "react";
import { Instagram, Plug, Unplug, Loader2, RefreshCcw, Trash2 } from "lucide-react";
import { set } from "date-fns";
import reqInstagram from "../../../../services/reqMultiTenant/reqInstagram";
import { id } from "date-fns/locale";
import { toast, ToastContainer } from "react-toastify";

//#region configurações do badge de status
const STATUS_MAP = {
	CONNECTED: { color: "bg-green-100 text-green-700", text: "Conectado", icon: <Plug size={16} /> },
	CONNECTING: {
		color: "bg-blue-100 text-blue-700",
		text: "Conectando...",
		icon: <Loader2 size={16} className="animate-spin" />,
	},
	DISCONNECTED: { color: "bg-red-100 text-red-700", text: "Desconectado", icon: <Unplug size={16} /> },
	LOADING: {
		color: "bg-blue-100 text-blue-700",
		text: "Carregando...",
		icon: <Loader2 size={16} className="animate-spin" />,
	},
	UNKNOWN: {
		color: "bg-gray-100 text-gray-700",
		text: "Indefinido",
		icon: <Loader2 size={16} className="animate-spin" />,
	},
	PENDING: {
		color: "bg-yellow-200 text-gray-700",
		text: "Pendente Conectar",
	},
};
//#endregion

const Avatar = ({ profilePicUrl, name }) => (
	<div className="w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-tr from-pink-500 to-yellow-400 flex items-center justify-center shadow">
		{profilePicUrl ? (
			<img src={profilePicUrl} alt={name || "Perfil"} className="w-full h-full object-cover " />
		) : (
			<Instagram className="w-10 h-10 text-white opacity-90" />
		)}
	</div>
);

const Row = ({ label, value }) => (
	<div className="flex items-center justify-between text-xs">
		<span className="text-gray-500">{label}</span>
		<span className="font-medium text-gray-800 truncate max-w-[60%] text-right">{value || "—"}</span>
	</div>
);

const InstanceCard = ({
	id_instagram,
	descricao,
	status: initialStatus = "LOADING",
	loading = false,
	onDisconnect,
	onDelete,
	onRefresh,
}) => {
	const [status, setStatus] = useState(initialStatus);
	const [profile, setProfile] = useState({});
	const badgeStatus = STATUS_MAP[status] || STATUS_MAP.UNKNOWN;

	const intervalRef = useRef(null); // 👈 guarda o setInterval ativo

	const CLIENT_ID = "1531937851550161";
	const REDIRECT_URI = "https://portal.szsistemas.com.br/auth/instagram/callback";
	const SCOPE = [
		"instagram_business_basic",
		"instagram_business_manage_messages",
		"instagram_business_manage_comments",
		"instagram_business_content_publish",
		"instagram_business_manage_insights",
	].join(",");

	const handleLogin = async (e) => {
		try {
			e.preventDefault();

			const data = {
				id_instagram,
				status_conexao: "CONNECTING",
			};

			await reqInstagram.UpdateStatus(data);
			// setStatus("CONNECTING"); // enquanto espera

			startPolling(); // 👈 reinicia o polling

			const authUrl = `https://www.instagram.com/oauth/authorize
			?force_reauth=true
			&client_id=${CLIENT_ID}
			&redirect_uri=${encodeURIComponent(REDIRECT_URI)}
			&response_type=code
			&scope=${encodeURIComponent(SCOPE)}
			&state=${encodeURIComponent(id_instagram)}`.replace(/\s+/g, "");

			// window.open(authUrl, "_blank", "width=600,height=700");
			window.open(authUrl, "instagramLogin", "width=600,height=700");
		} catch (error) {
			toast.error("Erro ao iniciar conexão com Instagram. " + error.message);
		}
	};

	const checkStatus = async () => {
		try {
			const statusConnection = await reqInstagram.statusConnection(id_instagram);

			if (statusConnection.data) {
				const data = statusConnection.data;

				if (data.status_conexao === "CONNECTED") {
					await fetchProfile();
				} else if (data.status_conexao === "PENDING") {
					setStatus("PENDING");
				} else if (data.status_conexao === "DISCONNECTED") {
					setStatus("DISCONNECTED");
				} else if (data.status_conexao === "CONNECTING") {
					setStatus("CONNECTING");
				}
			}
		} catch (error) {
			console.error("Erro ao verificar status:", error);
		}
	};

	const startPolling = () => {
		stopPolling();
		checkStatus();
		intervalRef.current = setInterval(checkStatus, 5000);
	};

	const stopPolling = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	};

	useEffect(() => {
		startPolling();
		return () => stopPolling();
	}, [id_instagram]);

	const fetchProfile = async () => {
		try {
			const responseProfile = await reqInstagram.profile(id_instagram);
			setProfile(responseProfile.data);
			setStatus("CONNECTED");
		} catch (error) {
			toast.error("Erro ao carregar perfil do Instagram. " + error.message);
		}
	};

	return (
		<div className="flex flex-col min-w-60 bg-white rounded-xl shadow-md p-3 border hover:shadow-lg transition">
			<ToastContainer />
			{/* Cabeçalho */}
			<div className="flex justify-between gap-3">
				<div className="min-w-0">
					<h2 className="text-sm font-semibold truncate">{descricao}</h2>
					<p className="text-xs text-gray-500 truncate">Instagram</p>
				</div>
				<span
					className={`h-8 flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badgeStatus.color}`}
				>
					{badgeStatus.icon}
					{badgeStatus.text}
				</span>
			</div>

			{/* Perfil */}
			<div className="flex items-center gap-3 mt-4">
				<Avatar profilePicUrl={profile?.profile_picture_url} name={profile?.name || profile?.username} />
				<div className="flex-1 min-w-0">
					<div className="text-sm font-medium truncate">{profile?.name || "—"}</div>
					<div className="text-xs text-gray-600 truncate">@{profile?.username || "—"}</div>
				</div>
			</div>

			{/* Infos rápidas */}
			<div className="mt-3 space-y-1.5">
				<Row label="Tipo" value={profile?.accountType} />
				<Row label="Conectado em" value={profile?.connectedAt} />
			</div>

			{/* Ações */}
			<div className="mt-4 flex flex-col gap-2">
				{status === "CONNECTED" ? (
					<>
						{/* Espaço para botões de atualizar e desconectar */}
					</>
				) : (
					<button
						onClick={(e) => handleLogin(e)}
						disabled={loading}
						className={`w-full h-9 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-yellow-400 text-white rounded-md shadow hover:opacity-95 transition ${
							loading && "opacity-50 cursor-not-allowed"
						}`}
					>
						<Plug size={16} />
						{loading ? "Conectando..." : "Conectar Instagram"}
					</button>
				)}

				{/* Apagar instância */}
				{onDelete && (
					<button
						onClick={onDelete}
						disabled={loading}
						className={`w-full h-9 inline-flex items-center justify-center gap-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition ${
							loading && "opacity-50 cursor-not-allowed"
						}`}
					>
						<Trash2 size={16} />
						{loading ? "Removendo..." : "Apagar Instância"}
					</button>
				)}
			</div>
		</div>
	);
};

export default InstanceCard;
